<script>
    import {dealADartPlayers} from '../store';
    import {createEventDispatcher, onMount, onDestroy} from "svelte";
    import {HistoryEvent} from "../dealadart.model";
    import {HistoryEventType} from "../history-events.enum";
    import {SegmentEnum} from "../segment.enum";

    const dispatch = createEventDispatcher();

    let segments = [SegmentEnum.SINGLE, SegmentEnum.DOUBLE, SegmentEnum.TRIPLE];
    let gameFinished = false;
    let activePlayerIndex = 0;
    let history = [];

    function reset() {
        gameFinished = false;
        activePlayerIndex = 0;
        history = [];
        dispatch('reset');
    }

    function newGame() {
        gameFinished = false;
        activePlayerIndex = 0;
        history = [];
        dispatch('newGame');
    }

    function segmentHit(segment) {
        if (gameFinished) return;
        let activePlayer = $dealADartPlayers[activePlayerIndex];
        if (!activePlayer) return;
        let segmentData = activePlayer.rounds.filter(item => item.segment === segment)[0];
        if (!segmentData || segmentData.hit) return;

        segmentData.hit = true;
        history = [...history, new HistoryEvent(HistoryEventType.SEGMENT_HIT, activePlayerIndex, segment)];

        activePlayer.rounds = [...activePlayer.rounds];

        dealADartPlayers.update(dealADartPlayers => {
            dealADartPlayers[activePlayerIndex].rounds = [...dealADartPlayers[activePlayerIndex].rounds];
            return [...dealADartPlayers];
        });

        checkWinner();
    }

    function checkWinner() {
        let activePlayer = $dealADartPlayers[activePlayerIndex];
        if (activePlayer && activePlayer.rounds.filter(item => item.hit === false).length === 0) {
            gameFinished = true;
        }
    }

    function nextPlayer() {
        if (gameFinished) return;
        if (activePlayerIndex === $dealADartPlayers.length - 1) {
            activePlayerIndex = 0;
        } else {
            activePlayerIndex = activePlayerIndex + 1;
        }

        history = [...history, new HistoryEvent(HistoryEventType.NEXT_PLAYER, activePlayerIndex, 0)];
    }

    function undo() {
        gameFinished = false;
        let lastHistoryItem = history[history.length - 1];
        if (!lastHistoryItem) {
            return;
        }
        let player = $dealADartPlayers[lastHistoryItem.playerIndex];
        let segment = lastHistoryItem.segment;

        if (lastHistoryItem.type === HistoryEventType.SEGMENT_HIT && player) {
            let roundSegment = player.rounds.filter(item => item.segment === segment)[0];
            if (roundSegment) {
                roundSegment.hit = false;
            }
        }

        if (lastHistoryItem.type === HistoryEventType.NEXT_PLAYER) {
            if (activePlayerIndex === 0) {
                activePlayerIndex = $dealADartPlayers.length - 1;
            } else {
                activePlayerIndex = activePlayerIndex - 1;
            }
        }

        history = history.slice(0, -1);
        dealADartPlayers.update(dealADartPlayers => {
            if (dealADartPlayers[activePlayerIndex]) {
                dealADartPlayers[activePlayerIndex].rounds = [...dealADartPlayers[activePlayerIndex].rounds];
            }
            return [...dealADartPlayers];
        });
    }

    function isPlayerActive(player, finished = gameFinished, activeIdx = activePlayerIndex) {
        return !finished && $dealADartPlayers[activeIdx]?.name === player?.name;
    }

    function isPlayerWinner(player, finished = gameFinished, activeIdx = activePlayerIndex) {
        return finished && $dealADartPlayers[activeIdx]?.name === player?.name;
    }

    function isSegmentHitForActivePlayer(segment, activeIdx = activePlayerIndex) {
        const activePlayer = $dealADartPlayers[activeIdx];
        if (!activePlayer) return false;
        const s = activePlayer.rounds.find(item => item.segment === segment);
        return s ? s.hit : false;
    }

    function handleKeydown(e) {
        // Only handle keydowns if not typing in an input
        if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
            return;
        }

        if (gameFinished) return;

        if (e.key === '1' || e.key === 's' || e.key === 'S') {
            e.preventDefault();
            segmentHit(SegmentEnum.SINGLE);
        } else if (e.key === '2' || e.key === 'd' || e.key === 'D') {
            e.preventDefault();
            segmentHit(SegmentEnum.DOUBLE);
        } else if (e.key === '3' || e.key === 't' || e.key === 'T') {
            e.preventDefault();
            segmentHit(SegmentEnum.TRIPLE);
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            nextPlayer();
        }
    }

    onMount(() => {
        window.addEventListener('keydown', handleKeydown);
    });

    onDestroy(() => {
        window.removeEventListener('keydown', handleKeydown);
    });
    function getPlayerSegmentRound(player, segment) {
        return player.rounds.find((r) => r.segment === segment);
    }
</script>

<div class="dealadart-screen w-full h-screen min-h-screen text-slate-900 font-sans antialiased flex flex-col p-3 sm:p-4 md:p-5 lg:p-6 box-border bg-[#f0ece9]">
    <!-- BEGIN: TopControlsBar -->
    <header class="w-full max-w-[1600px] mx-auto bg-white rounded-2xl sm:rounded-3xl shadow-sm px-4 sm:px-6 py-3 mb-3 sm:mb-4 border border-black/5 flex flex-wrap items-center justify-between gap-4 shrink-0">
        <!-- Brand & Game Badge -->
        <div class="flex items-center gap-4 sm:gap-6 min-w-[200px]">
            <button class="inline-flex items-center transition-opacity hover:opacity-85 focus:outline-none" on:click={newGame} title="Yuma Home">
                <img alt="Yuma Logo" class="h-8 sm:h-9 w-auto object-contain" src="images/yuma.png" />
            </button>
            <div class="h-8 w-px bg-slate-200"></div>
        </div>

        <!-- Center Controls: Hit Actions & Next Player -->
        <div class="flex items-center justify-center flex-1 max-w-2xl mx-auto order-last md:order-none w-full md:w-auto">
            <div class="flex items-center gap-2 bg-stone-100/80 p-1.5 rounded-2xl border border-slate-200 shadow-inner">
                <!-- Next Player Button -->
                <button
                    class="h-11 inline-flex items-center justify-center gap-2 bg-[#eba9ff] hover:bg-[#e493fb] active:scale-[0.98] text-[#005d46] font-bold px-4 sm:px-5 rounded-xl transition-all shadow-sm border border-purple-300/30 whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    type="button"
                    on:click={nextPlayer}
                    disabled={gameFinished}
                    title="Next player (shortcut: Enter)"
                >
                    <span>Next player</span>
                    <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
                </button>
            </div>
        </div>

        <!-- Right Quick Actions (Undo, Reset Round, Home) -->
        <div class="flex items-center justify-end gap-2.5 min-w-[200px]">
            <!-- Undo Button -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-pink-50 hover:bg-pink-100 text-rose-600 transition-colors border border-rose-200/50 cursor-pointer active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Undo last action"
                type="button"
                on:click={undo}
                disabled={history.length === 0}
            >
                <span class="material-symbols-outlined text-[20px]">undo</span>
            </button>
            <!-- New Round Button -->
            <button
                class="h-11 inline-flex items-center gap-2 bg-[#6434da] hover:bg-[#5229b5] text-white text-sm font-semibold px-4 rounded-xl transition-colors shadow-sm cursor-pointer active:scale-95"
                type="button"
                on:click={reset}
                title="Reset game"
            >
                <span class="material-symbols-outlined text-[18px]">restart_alt</span>
                <span class="hidden sm:inline">New round</span>
            </button>
            <!-- Exit to Setup / Lobby -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border border-slate-200 cursor-pointer active:scale-95"
                title="Exit to Setup"
                type="button"
                on:click={newGame}
            >
                <span class="material-symbols-outlined text-[20px]">home</span>
            </button>
        </div>
    </header>
    <!-- END: TopControlsBar -->

    <!-- BEGIN: Main Scoreboard Table -->
    <main class="w-full max-w-[1600px] mx-auto flex-1 flex flex-col min-h-0 bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-black/5 overflow-hidden">
        <div class="w-full h-full flex-1 flex flex-col min-h-0 overflow-x-auto">
            <table class="dealadart-table w-full h-full flex-1 border-collapse select-none">
                <!-- THEAD: Players Header -->
                <thead>
                    <tr class="h-16 sm:h-20 border-b border-emerald-900/10">
                        <!-- Left Header: Game Title -->
                        <th class="w-48 sm:w-60 md:w-72 bg-[#005d46] text-white p-3 sm:p-4 text-left align-middle border-r border-emerald-800/40" style="text-align: left;">
                            <div class="flex items-center justify-start gap-2 sm:gap-3 text-left">
                                <span class="material-symbols-outlined text-[#21e467] text-[24px] sm:text-[28px] shrink-0">style</span>
                                <div class="text-left">
                                    <div class="text-base sm:text-lg md:text-xl font-black font-headline tracking-tight text-white leading-tight">
                                        Deal a dart
                                    </div>
                                    <div class="text-[11px] sm:text-xs text-emerald-200/80 font-medium">
                                        Hit all 3 cards to win
                                    </div>
                                </div>
                            </div>
                        </th>

                        <!-- Player Columns -->
                        {#each $dealADartPlayers as player, idx}
                            <th
                                class="px-4 py-2.5 text-center align-middle transition-colors border-r last:border-r-0 {
                                    isPlayerWinner(player, gameFinished, activePlayerIndex)
                                        ? 'bg-[#21e467] text-[#005d46] border-emerald-600/40 shadow-sm'
                                        : isPlayerActive(player, gameFinished, activePlayerIndex)
                                            ? 'bg-[#fff8e7] text-amber-950 border-amber-200/80 shadow-inner'
                                            : 'bg-[#005d46] text-white border-emerald-900/30'
                                }"
                                scope="col"
                            >
                                <div class="flex items-center justify-center gap-2 flex-wrap">
                                    {#if isPlayerActive(player, gameFinished, activePlayerIndex) && !isPlayerWinner(player, gameFinished, activePlayerIndex)}
                                        <span class="inline-block w-2.5 h-2.5 rounded-full bg-[#21e467] border-2 border-emerald-700 animate-pulse"></span>
                                    {/if}
                                    <span class="text-xl sm:text-2xl font-bold font-headline truncate">
                                        {player?.name}
                                    </span>
                                    {#if isPlayerWinner(player, gameFinished, activePlayerIndex)}
                                        <span class="text-xl sm:text-2xl leading-none select-none inline-flex items-center">
                                            🏆
                                        </span>
                                    {/if}
                                </div>
                            </th>
                        {/each}
                    </tr>
                </thead>

                <!-- TBODY: Segment Rows (Single, Double, Triple) -->
                <tbody>
                    {#each segments as segment, segmentIdx}
                        <tr class="segment-row border-b last:border-b-0 border-slate-200/80">
                            <!-- Left: Segment Action Button Column -->
                            <td class="w-48 sm:w-60 md:w-72 p-3 sm:p-4 bg-stone-50/60 border-r border-slate-200 align-middle">
                                <button
                                    class="w-full py-3 sm:py-4 px-4 rounded-2xl flex items-center justify-between font-headline font-black text-base sm:text-lg md:text-xl transition-all shadow-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed
                                    bg-[#6434da] hover:bg-[#5229b5] text-white border border-emerald-900/30
                                    "
                                    disabled={gameFinished || isSegmentHitForActivePlayer(segment, activePlayerIndex)}
                                    on:click={() => segmentHit(segment)}
                                    type="button"
                                >
                                    <div class="flex items-center gap-2">
                                        <span class="w-6 h-6 rounded-lg bg-black/10 flex items-center justify-center text-xs font-mono font-bold">
                                            {segmentIdx + 1}
                                        </span>
                                        <span>{segment[0].toUpperCase() + segment.substring(1).toLowerCase()}</span>
                                    </div>
                                    <span class="material-symbols-outlined text-[20px] sm:text-[24px]">
                                        {isSegmentHitForActivePlayer(segment, activePlayerIndex) ? 'check' : 'ads_click'}
                                    </span>
                                </button>
                            </td>

                            <!-- Players' Numbers & Hit States -->
                            {#each $dealADartPlayers as player}
                                <td
                                    class="p-3 sm:p-4 text-center align-middle border-r last:border-r-0 border-slate-200 transition-colors {
                                        isPlayerWinner(player, gameFinished, activePlayerIndex)
                                            ? 'bg-emerald-50/50'
                                            : isPlayerActive(player, gameFinished, activePlayerIndex)
                                                ? 'bg-amber-50/40'
                                                : ''
                                    }"
                                >
                                    <div class="flex flex-col items-center justify-center gap-1 w-full h-full">
                                        {#if getPlayerSegmentRound(player, segment)?.hit}
                                            <div class="inline-flex items-center justify-center gap-2 bg-[#21e467]/20 text-[#005d46] border-2 border-[#21e467] px-4 py-2 sm:px-6 sm:py-3 rounded-2xl shadow-sm animate-scale-in">
                                                <span class="text-3xl sm:text-4xl md:text-5xl font-black font-headline tracking-tight">
                                                    {getPlayerSegmentRound(player, segment)?.number || ''}
                                                </span>
                                                <span class="material-symbols-outlined text-[#005d46] text-[28px] sm:text-[36px] font-bold">
                                                    check_circle
                                                </span>
                                            </div>
                                        {:else}
                                            <div class="inline-flex items-center justify-center px-4 py-2 sm:px-6 sm:py-3 rounded-2xl border-2 border-dashed border-slate-300 bg-white/80 text-slate-800">
                                                <span class="text-3xl sm:text-4xl md:text-5xl font-black font-headline tracking-tight">
                                                    {getPlayerSegmentRound(player, segment)?.number || ''}
                                                </span>
                                            </div>
                                        {/if}
                                    </div>
                                </td>
                            {/each}
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>

        <!-- Footer Info Strip -->
        <div class="bg-stone-50 px-4 sm:px-6 py-2.5 border-t border-slate-200/80 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500 shrink-0">
            <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-[16px] text-[#005d46]">info</span>
                <span>Hit your assigned <strong>Single</strong>, <strong>Double</strong>, and <strong>Triple</strong> numbers. First player to hit all 3 wins!</span>
            </div>
            <div class="flex items-center gap-3 font-medium">
                <span>Shortcuts: <kbd class="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-slate-700 font-mono text-[10px]">1</kbd> Single &nbsp; <kbd class="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-slate-700 font-mono text-[10px]">2</kbd> Double &nbsp; <kbd class="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-slate-700 font-mono text-[10px]">3</kbd> Triple &nbsp; <kbd class="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-slate-700 font-mono text-[10px]">Enter</kbd> Next</span>
            </div>
        </div>
    </main>
    <!-- END: Main Scoreboard Table -->
</div>

<style>
    .dealadart-table {
        table-layout: fixed;
    }

    .dealadart-table th,
    .dealadart-table td {
        vertical-align: middle !important;
    }

    .segment-row {
        height: 1%;
    }

    @keyframes scaleIn {
        0% {
            transform: scale(0.9);
            opacity: 0.7;
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    }

    .animate-scale-in {
        animation: scaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }
</style>
